import React, { useState } from "react";

import { Box, Button, Flex, Stack, Text, VStack } from "@chakra-ui/react";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { FiClock, FiAlertCircle, FiEye } from "react-icons/fi";
import { useSelector } from "react-redux";
import { PrintReceiptButton } from "./PrintReceiptButton";
import { useSteps } from "chakra-ui-steps";

// Truthful heading/icon per resident payment outcome — never show a success
// checkmark for a state that isn't actually CONFIRMED (Checkpoint 2B2 §16).
const STATE_PRESENTATION = {
  CONFIRMED: {
    heading: "Payment completed",
    icon: IoIosCheckmarkCircleOutline,
    color: "green.500",
  },
  PENDING: {
    heading: "Payment processing",
    icon: FiClock,
    color: "orange.400",
  },
  PROCESSING: {
    heading: "Payment processing",
    icon: FiClock,
    color: "orange.400",
  },
  FAILED: {
    heading: "Payment could not be completed",
    icon: FiAlertCircle,
    color: "red.500",
  },
  REFUNDED: {
    heading: "Payment refunded",
    icon: FiAlertCircle,
    color: "red.400",
  },
  RECONCILIATION_REQUIRED: {
    heading: "Payment requires review",
    icon: FiEye,
    color: "orange.500",
  },
  OUTCOME_UNKNOWN: {
    heading: "Payment confirmation is still being reviewed",
    icon: FiClock,
    color: "orange.400",
  },
};

export const SuccessPage = ({
  userProfile,
  unit,
  unitPurchased,
  amountPaid,
  amountCal,
  utilityType,
  token,
  status = "CONFIRMED",
}) => {
  const { nextStep } = useSteps({
    initialStep: 0,
  });
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const pageColor = useSelector(
    (state) => state?.switchTab?.brandColor?.brandColor,
  );

  const presentation = STATE_PRESENTATION[status] || STATE_PRESENTATION.OUTCOME_UNKNOWN;
  const Icon = presentation.icon;
  const isConfirmed = status === "CONFIRMED";

  return (
    <Flex align={"center"} justify={"center"} w="100%" mt="30px">
      <VStack w="100%">
        <Icon size={"5em"} color={isConfirmed ? pageColor : presentation.color} />
        <Stack align={"center"} spacing={0}>
          <Text color={"#050509"} fontSize="22px" fontWeight={"bold"} textAlign="center">
            {presentation.heading}
          </Text>
          {status === "RECONCILIATION_REQUIRED" && (
            <Text color="gray.600" fontSize="14px" textAlign="center" mt="8px" maxW="360px">
              Your payment was received. Please do not pay again — our team is
              confirming your token and will notify you shortly.
            </Text>
          )}
          {status === "REFUNDED" && (
            <Text color="gray.600" fontSize="14px" textAlign="center" mt="8px" maxW="360px">
              Your payment could not be processed and has been refunded. You
              should receive it within 1–3 business days.
            </Text>
          )}
        </Stack>

        {isConfirmed && token && (
          <Box
            bg="#F0FFF4"
            border="1px solid #68D391"
            borderRadius="8px"
            p="16px 24px"
            mt="10px"
            textAlign="center"
            w="100%"
          >
            <Text
              fontSize="12px"
              color="#2F855A"
              fontWeight="semibold"
              mb="4px"
            >
              Your Token
            </Text>
            <Text
              fontSize="22px"
              fontWeight="bold"
              letterSpacing="widest"
              color="#22543D"
            >
              {token}
            </Text>
            <Button
              size="xs"
              mt="8px"
              colorScheme="green"
              variant="outline"
              onClick={() => navigator.clipboard.writeText(token)}
            >
              Copy Token
            </Button>
          </Box>
        )}

        <PrintReceiptButton
          nextStep={nextStep}
          userProfile={userProfile}
          showSuccessPage={showSuccessPage}
          setShowSuccessPage={setShowSuccessPage}
          amountPaid={amountPaid}
          unitPurchased={unitPurchased}
          unit={unit}
          amountCal={amountCal}
          utilityType={utilityType}
        />
      </VStack>
    </Flex>
  );
};
